import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Trash2, Loader2, GripVertical, Eye, EyeOff, Save } from "lucide-react";
import { TOKEN_KEY } from "@/pages/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_IMAGES = 9;

const AdminImages = () => {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const api = useMemo(
    () => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    [token]
  );
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef(null);

  const fetchAll = async () => {
    if (!token) { navigate("/admin/login"); return; }
    setLoading(true);
    try {
      const r = await api.get("/admin/gallery");
      setImages(r.data);
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { document.title = "CRM · Impressionen"; fetchAll(); }, []);

  const upload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (images.length >= MAX_IMAGES) {
      toast.error(`Maximum von ${MAX_IMAGES} Bildern erreicht.`);
      return;
    }
    const toUpload = Array.from(fileList).slice(0, MAX_IMAGES - images.length);
    setUploading(true);
    let ok = 0;
    for (const file of toUpload) {
      if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
        toast.error(`${file.name}: Nur JPG, PNG oder WEBP.`);
        continue;
      }
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post("/admin/gallery/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || "Upload fehlgeschlagen."}`);
      }
    }
    if (ok > 0) toast.success(`${ok} Bild${ok > 1 ? "er" : ""} hochgeladen.`);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    fetchAll();
  };

  const patch = async (img, changes) => {
    setSavingId(img.id);
    try {
      const r = await api.patch(`/admin/gallery/${img.id}`, changes);
      setImages(imgs => imgs.map(x => x.id === img.id ? r.data : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bild wirklich entfernen?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Gelöscht.");
      fetchAll();
    } catch { toast.error("Löschen fehlgeschlagen."); }
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const next = [...images];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setImages(next);
    setDragIdx(null);
    try {
      await api.post("/admin/gallery/reorder", { ids: next.map(x => x.id) });
    } catch { toast.error("Reihenfolge konnte nicht gespeichert werden."); fetchAll(); }
  };

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-images-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-2">
              <ArrowLeft size={12} /> CRM
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Impressionen</h1>
            <p className="text-[#A8A58F] text-sm mt-1">
              {images.length} / {MAX_IMAGES} Bilder · Drag &amp; Drop zum Sortieren · JPG/PNG/WEBP bis 8 MB
            </p>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={(e) => upload(e.target.files)}
              className="hidden"
              data-testid="upload-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading || images.length >= MAX_IMAGES}
              className="inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-full px-5 py-2.5 text-sm disabled:opacity-50"
              data-testid="upload-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
              {uploading ? "Lade hoch…" : "Bild hochladen"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-[#C9C6B4]">Lade…</div>
        ) : images.length === 0 ? (
          <div
            onClick={() => fileRef.current?.click()}
            className="rounded-2xl border-2 border-dashed border-white/20 bg-[#4E5A3B]/40 p-10 text-center text-[#C9C6B4] cursor-pointer hover:bg-[#4E5A3B]/60"
            data-testid="empty-drop"
          >
            <ImagePlus size={28} className="mx-auto mb-3 text-[#A8A58F]" />
            Noch keine Bilder. Klicke hier oder auf &bdquo;Bild hochladen&ldquo;.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((img, i) => (
              <div
                key={img.id}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(i)}
                className={`rounded-2xl border border-white/12 bg-[#4E5A3B]/50 overflow-hidden ${dragIdx === i ? "opacity-40" : ""}`}
                data-testid={`image-${img.id}`}
              >
                <div className="relative aspect-[4/5] bg-black/40 overflow-hidden">
                  <img
                    src={`${BACKEND_URL}${img.url}`}
                    alt={img.alt || img.title || ""}
                    className={`w-full h-full object-cover ${img.active ? "" : "grayscale opacity-60"}`}
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-black/60 backdrop-blur-sm rounded-full px-2 py-0.5">
                    <GripVertical size={10} /> #{i + 1}
                  </div>
                </div>
                <div className="p-3 space-y-2">
                  <input
                    defaultValue={img.title || ""}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (img.title || "")) patch(img, { title: v });
                    }}
                    placeholder="Titel (z. B. Transformation 1)"
                    className="w-full bg-[#3A4230]/60 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-[#F3F1E8] placeholder:text-[#A8A58F]"
                    data-testid={`title-${img.id}`}
                  />
                  <input
                    defaultValue={img.alt || ""}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (img.alt || "")) patch(img, { alt: v });
                    }}
                    placeholder="Alt-Text (SEO/Accessibility)"
                    className="w-full bg-[#3A4230]/60 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-[#F3F1E8] placeholder:text-[#A8A58F]"
                    data-testid={`alt-${img.id}`}
                  />
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <button
                      onClick={() => patch(img, { active: !img.active })}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-[#C9C6B4] hover:text-[#F3F1E8]"
                      data-testid={`toggle-${img.id}`}
                    >
                      {img.active ? <Eye size={12} /> : <EyeOff size={12} />}
                      {img.active ? "Sichtbar" : "Verborgen"}
                      {savingId === img.id && <Loader2 size={10} className="animate-spin" />}
                    </button>
                    <button
                      onClick={() => remove(img.id)}
                      className="text-[11px] font-bold bg-red-500/20 border border-red-500/40 hover:bg-red-500/30 text-red-200 rounded-lg px-3 py-1"
                      data-testid={`delete-${img.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 text-[11px] text-[#A8A58F]">
          <Save size={11} className="inline mr-1" />
          Änderungen an Titel &amp; Alt-Text werden beim Verlassen des Feldes automatisch gespeichert.
        </div>
      </div>
    </div>
  );
};

export default AdminImages;
